import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Container,
  Alert,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Lock as LockIcon,
  Refresh as RenewIcon,
  ArrowUpward as UpgradeIcon,
  Schedule as ScheduleIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanUpgrade } from '../../contexts/PlanUpgradeContext';
import RenewProModal from './RenewProModal';
import UpgradeToProModal from './UpgradeToProModal';
import api from '../../services/api';
import { useTheme } from '@mui/material/styles';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

// --- RENDER HELPERS ---

// Renders the expired subscription content
function renderExpiredContent({ currentPlan, user, getAlertMessage, getActionButtons, theme }) {
  return (
    <>
      <Box sx={{ mb: 3 }}>
        <WarningIcon 
          sx={{ 
            fontSize: 80, 
            color: '#ff6b6b',
            filter: 'drop-shadow(0 4px 8px rgba(255, 107, 107, 0.3))'
          }} 
        />
      </Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}
      >
        Subscription Expired
      </Typography>
      <Alert 
        severity="error" 
        icon={<LockIcon />}
        sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { fontWeight: 500 } }}
      >
        {getAlertMessage()}
      </Alert>
      {/* Subscription Details */}
      {currentPlan && (
        <Box
          sx={{
            width: '100%',
            maxWidth: 340,
            mx: 'auto',
            bgcolor: theme.palette.background.default,
            borderRadius: 8,
            p: 2,
            mb: 2,
            boxShadow: theme.shadows[0],
            textAlign: 'center',
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{ letterSpacing: 0.5, textAlign: 'center' }}
          >
            Current Plan: {currentPlan.plan?.name ?? currentPlan.plan_name}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1 }}>
            <Chip
              label={
                (currentPlan.plan?.end_date ?? currentPlan.end_date)
                  ? `Expired: ${new Date(currentPlan.plan?.end_date ?? currentPlan.end_date).toLocaleDateString()}`
                  : 'Expired'
              }
              color="error"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </Box>
      )}
      {/* Consistent Action Buttons and Support for Admins */}
      {user?.role === 'AD' && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center', mt: 2 }}>
            {getActionButtons()}
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 3, opacity: 0.8 }}
          >
            Need help?{' '}
            <a
              href="https://salesgro.ai/contact"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#8140d7', textDecoration: 'underline', fontWeight: 500 }}
            >
              Contact our support team
            </a>{' '}
            for assistance.
          </Typography>
        </>
      )}
    </>
  );
}

// Renders the submitting state
function renderSubmittingContent() {
  return (
    <>
      <Box sx={{ mb: 3 }}>
        <CircularProgress size={80} color="primary" />
      </Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}
      >
        Submitting your request...
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Please wait while we process your subscription request.
      </Typography>
    </>
  );
}

// Renders the pending request state
function renderPendingContent({ requestDetails, currentPlan, user }) {
  const reqType = requestDetails?.request_type;
  const isAdmin = user?.role === 'AD';

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <ScheduleIcon 
          sx={{ 
            fontSize: 80, 
            color: '#ff9800',
            filter: 'drop-shadow(0 4px 8px rgba(255, 152, 0, 0.3))'
          }} 
        />
      </Box>
      <Typography
        variant="h5"
        component="h1"
        gutterBottom
        sx={{ fontWeight: 600, color: '#2c3e50', mb: 2 }}
      >
        {reqType === 'RW' ? (
          isAdmin
            ? <>Your renewal request has been submitted and is under review.</>
            : <>Your admin has submitted a renewal request. Please wait for approval.</>
        ) : (
          isAdmin
            ? <>
                Your upgrade request to{' '}
                <b style={{ color: '#7c4dff' }}>{requestDetails.plan_name || 'selected plan'}</b> has been submitted and is under review.
              </>
            : <>
                Your admin has submitted a request to upgrade to{' '}
                <b style={{ color: '#7c4dff' }}>{requestDetails.plan_name || 'selected plan'}</b>. Please wait for approval.
              </>
        )}
      </Typography>
      <Box sx={{ mb: 3, p: 0, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{
          bgcolor: '#fff',
          borderRadius: 4,
          px: 4,
          py: 3,
          minWidth: 340,
          maxWidth: 420,
          boxShadow: '0 2px 12px 0 rgba(44,62,80,0.06)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          border: 'none',
        }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: '#263238', mb: 1, textAlign: 'center' }}
          >
            Subscription Expired
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Chip
              icon={<WorkspacePremiumIcon sx={{ color: '#388e3c' }} />}
              label={
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="#388e3c"
                  sx={{ letterSpacing: 0.5, textTransform: 'capitalize' }}
                >
                  {(currentPlan.plan?.name ?? currentPlan.plan_name) || (currentPlan.plan?.type ?? currentPlan.plan_type)}
                </Typography>
              }
              sx={{
                bgcolor: '#f5f7fa',
                color: '#388e3c',
                px: 3,
                py: 1.5,
                fontSize: 22,
                borderRadius: 99,
                minWidth: 180,
                boxShadow: '0 2px 8px 0 rgba(44,62,80,0.10)',
                height: 48,
              }}
              size="medium"
            />
          </Box>
          <Typography variant="body2" color="#607080" sx={{ mb: 0.5, fontWeight: 500 }}>
            Request submitted at: {requestDetails.submitted_at}
          </Typography>
          <Typography variant="body2" color="#607080" sx={{ fontWeight: 500 }}>
            Request status: {requestDetails.status}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

// Main content rendering function
function renderContent(props) {
  const { requestStatus, requestDetails } = props;
  
  // If there's a pending request, show pending content
  if (requestStatus === 'pending' && requestDetails) {
    return renderPendingContent(props);
  }
  
  // Default: expired content (only when no pending request)
  return renderExpiredContent(props);
}

const SubscriptionOverlay = ({ currentPlan, company_name }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { openPlansModal, isPlansModalOpen, wasUpgradeSubmitted, setWasUpgradeSubmitted } = usePlanUpgrade();
  const [showRenewProModal, setShowRenewProModal] = useState(false);
  const [showUpgradeProModal, setShowUpgradeProModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null); // 'submitting', 'success', 'pending'
  const [requestDetails, setRequestDetails] = useState(null);
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
  const theme = useTheme();

  const planType = currentPlan?.plan?.type ?? currentPlan?.plan?.plan_type ?? currentPlan?.plan_type ?? 'free';
  const isPro = String(planType || '').toLowerCase() === 'pro';
  const isFree = String(planType || '').toLowerCase() === 'free';

  // Check if subscription is expired
  const isExpired = typeof (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) === 'number' && (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) <= 0;

  // Check if current page should be allowed (settings/subscription)
  const isAllowedPage = () => {
    const allowedPaths = ['/settings/subscription'];
    return allowedPaths.some(path => location.pathname === path);
  };

  // Check if there's a pending request
  const checkPendingRequest = async () => {
    try {
      const response = await api.get('subscription/new_request_allowed/');
      if (response.status === 200 && response.data.allowed === false) {
        setRequestStatus('pending');
        const response_data = response.data;
        const request_data = response_data.request_data;
        // Use new request_data if present
        if (request_data) {
          setRequestDetails({
            request_type: request_data.request_type,
            plan_name: request_data.request_plan_name,
            plan_id: request_data.request_plan_id,
            submitted_at: request_data.request_submitted_at,
            status: request_data.request_status,
            is_allowed: response_data.allowed,
          });
        } else {
          setRequestDetails({
            plan_type: currentPlan?.plan?.type ?? currentPlan?.plan_type,
            submitted_at: new Date().toISOString(),
            status: 'pending',
            request_type: null,
            is_allowed: response_data.allowed,
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };


  // Handle request failure
  const handleRequestFailure = (err) => {
    setRequestStatus(null);
    setErrorDialog({
      open: true,
      message: err?.response?.data?.message || 'Request failed. Please try again.',
    });
  };

  // Retry request
  const handleRetry = () => {
    setRequestStatus(null);
    setRequestDetails(null);
    setErrorDialog({ open: false, message: '' });
  };

  // Check for pending requests on mount and when plans modal closes
  useEffect(() => {
    if (isExpired && !isAllowedPage()) {
      checkPendingRequest();
    }
  }, [isExpired, location.pathname, isPlansModalOpen]);

  // Check for pending requests when plans modal closes and upgrade was submitted
  useEffect(() => {
    if (!isPlansModalOpen && wasUpgradeSubmitted && isExpired && !isAllowedPage()) {
      // Small delay to ensure the API has processed the request
      const timer = setTimeout(() => {
        checkPendingRequest();
        setWasUpgradeSubmitted(false); // Reset the flag after checking
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isPlansModalOpen, wasUpgradeSubmitted]);

  // Right-click / devtools blocking disabled for testing (e.g. Network tab)
  // useEffect(() => {
  //   if (isExpired && !isAllowedPage()) {
  //     const handleContextMenu = (e) => e.preventDefault();
  //     document.addEventListener('contextmenu', handleContextMenu);
  //     const handleKeyDown = (e) => {
  //       if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'i') || (e.metaKey && e.altKey && e.key.toLowerCase() === 'i')) e.preventDefault();
  //     };
  //     document.addEventListener('keydown', handleKeyDown);
  //     return () => {
  //       document.removeEventListener('contextmenu', handleContextMenu);
  //       document.removeEventListener('keydown', handleKeyDown);
  //     };
  //   }
  // }, [isExpired, location.pathname]);

  // Show loader while fetching subscription data
  if (currentPlan == null || loading) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.15)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress color="primary" size={48} />
      </Box>
    );
  }

  // If subscription is not expired or we're on an allowed page, don't show overlay
  if (!isExpired || isAllowedPage()) {
    return null;
  }

  // Get alert message: AD sees action prompt; non-AD sees contact-admin message
  const getAlertMessage = () => {
    if (user?.role === 'AD') {
      if (isPro) {
        return 'Your subscription has expired. Please renew the subscription to continue.';
      }
      if (isFree) {
        return 'Your subscription has expired. Please upgrade the subscription to continue.';
      }
      return 'Your subscription has expired. Please renew or upgrade the subscription to continue.';
    }
    if (isFree) {
      return 'Your Free subscription has expired. Please contact your admin to upgrade the subscription to continue.';
    }
    if (isPro) {
      return 'Your Pro subscription has expired. Please contact your admin to renew the subscription to continue.';
    }
    return 'Your subscription has expired. Please contact your admin.';
  };

  // Action buttons: only for AD — Renew opens RenewProModal, Upgrade opens UpgradeToProModal
  const getActionButtons = () => {
    if (user?.role !== 'AD') return null;
    if (isFree) {
      return (
        <Button
          variant="primaryLinkOutline"
          color="primary"
          sx={{ minWidth: 120, fontWeight: 600 }}
          startIcon={<UpgradeIcon />}
          onClick={() => setShowUpgradeProModal(true)}
        >
          Upgrade
        </Button>
      );
    }
    if (isPro) {
      return (
        <Button
          variant="secondaryOutlined"
          color="primary"
          size="small"
          sx={{ minWidth: 120, fontWeight: 600 }}
          startIcon={<RenewIcon />}
          onClick={() => setShowRenewProModal(true)}
        >
          Renew
        </Button>
      );
    }
    return null;
  };

  // Always render the overlay background when expired
  return (
    <>
      <Box
        id="subscription-overlay"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          border: (requestStatus === 'pending' && requestDetails?.status === 'PE') ? '2px solid #2196f3' : undefined,
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.3s ease-in-out',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'scale(0.95)',
            },
            '100%': {
              opacity: 1,
              transform: 'scale(1)',
            },
          },
        }}
      >
        {/* Show overlay content when no Renew/Upgrade modal is open, or when pending request */}
        {(!(showRenewProModal || showUpgradeProModal) || requestStatus === 'pending') && (
          <Container maxWidth="sm">
            <Paper
              elevation={24}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                animation: 'slideUp 0.4s ease-out',
                '@keyframes slideUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px) scale(0.95)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0) scale(1)',
                  },
                },
              }}
            >
              {renderContent({ currentPlan, user, getAlertMessage, getActionButtons, theme, requestStatus, requestDetails })}
              <Divider sx={{ my: 3 }} />
              {/* Footer: Support left, Logout right */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  color="default"
                  variant="clearBtnOutlined"
                  sx={{ minWidth: 120, fontWeight: 600, ml: 'auto' }}
                  startIcon={<PowerSettingsNewIcon sx={{ fontSize: '18px' }}/>}
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </Box>
            </Paper>
          </Container>
        )}
      </Box>

      {/* Renew Pro modal — for expired Pro plan (AD only); on close, overlay shows again */}
      {isPro && (
        <RenewProModal
          open={showRenewProModal}
          onClose={() => setShowRenewProModal(false)}
        />
      )}

      {/* Upgrade to Pro modal — for expired Free plan (AD only); on close, overlay shows again */}
      {isFree && (
        <UpgradeToProModal
          open={showUpgradeProModal}
          onClose={() => setShowUpgradeProModal(false)}
        />
      )}

      {/* Error Dialog */}
      <Dialog open={errorDialog.open} onClose={() => setErrorDialog({ open: false, message: '' })}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography>{errorDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialog({ open: false, message: '' })}>Close</Button>
          <Button onClick={handleRetry} variant="contained">Retry</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscriptionOverlay; 